import React, { useEffect, useState } from 'react'

import { useHistory } from 'react-router-dom'

import { Box, Button, Grid, TextField } from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { Controller, FormContext, useForm } from 'react-hook-form'

import {
  AutocompleteTypeAndSearch,
  Card,
  FileUploadInput,
  Label,
  LoadingFeedback,
  Select,
  SwitchController,
} from 'components'

import { reverse } from 'named-urls'

import helpers from 'helpers'

import useFetch from 'hooks/useFetch'

import useSnackbar from 'hooks/useSnackbar'

import { routes } from 'Routes'
import constants from 'constants/index'
import * as service from 'service'
import schema from './schema'

const KpiForm = ({ monitoringManagementKpi, refresh, isShow }) => {
  const [isLoading, setIsLoading] = useState(false)

  const history = useHistory()

  const snackbar = useSnackbar()

  const formMethods = useForm({
    validationSchema: schema(),
    defaultValues: {
      active: true,
      name: '',
      assigneeId: null,
      description: '',
      departmentId: '',
      toDo: '',
      methodology: '',
      goal: '',
      goalMin: '',
      goalMax: '',
      goalOperator: '',
      measurementUnit: '',
      periodicity: '',
      priority: '',
      periodicityDay: '',
      startDate: '',
      endDate: '',
      formula: '',
      dataSource: '',
      attachments: undefined,
    },
  })

  const { control, errors, handleSubmit, formState, watch } = formMethods

  const { dirtyFields } = formState

  const { response: responseDepartments, isLoading: isDepartmentsLoading } =
    useFetch(service.dponet.departments.get, {
      perPage: 1000,
      order: 'name ASC',
      minimal: true,
    })

  const responseUsers = async (inputValue) => {
    try {
      const response = await service.dponet.users.get({
        name: inputValue,
        perPage: 10000000,
        minimal: true,
        order: 'name ASC',
      })

      return response?.data?.users || []
    } catch {
      return []
    }
  }

  const departments = responseDepartments?.data?.departments || []

  const {
    PRIORITY_OPTIONS,
    MEASUREMENT_UNIT_OPTIONS,
    GOAL_OPERATOR_OPTIONS,
    PERIODICITY_OPTIONS,
    IN_BETWEEN_GOAL_OPERATOR_ENUM,
  } = constants.monitoringManagementKpi

  const isEdit = !!monitoringManagementKpi

  const goalOperator = watch('goalOperator')

  const isInBetween = goalOperator === IN_BETWEEN_GOAL_OPERATOR_ENUM

  const onSubmit = async (data) => {
    setIsLoading(true)

    try {
      if (!isEdit) {
        const endDate = helpers.formatters.toISOStringVerify(data?.endDate)
        const startDate = helpers.formatters.toISOStringVerify(data?.startDate)
        const periodicityDay = helpers.formatters.toISOStringVerify(
          data?.periodicityDay,
        )

        const response = await service.dponet.monitoringManagementKpi.create({
          ...data,
          assigneeId: data?.assigneeId?.id,
          departmentId: data?.departmentId,
          endDate,
          startDate,
          periodicityDay,
        })

        const monitoringManagementKpi = response?.data?.monitoringManagementKpi

        history.push(
          reverse(routes.kpi.edit, {
            monitoringManagementKpiId: monitoringManagementKpi?.id,
          }),
        )
        snackbar.open({
          message: 'KPI criado com sucesso.',
          variant: 'success',
        })
      }

      if (isEdit && dirtyFields.size > 0) {
        const changedData = Array.from(dirtyFields).reduce((acc, key) => {
          acc[key] = data[key]
          return acc
        }, {})

        if (changedData.endDate !== undefined)
          changedData.endDate = helpers.formatters.toISOStringVerify(
            changedData.endDate,
          )
        if (changedData.startDate !== undefined)
          changedData.startDate = helpers.formatters.toISOStringVerify(
            changedData.startDate,
          )
        if (changedData.periodicityDay !== undefined)
          changedData.periodicityDay = helpers.formatters.toISOStringVerify(
            changedData.periodicityDay,
          )
        if (changedData.assigneeId !== undefined)
          changedData.assigneeId = data?.assigneeId?.id

        await service.dponet.monitoringManagementKpi.update({
          monitoringManagementKpiId: monitoringManagementKpi?.id,
          ...changedData,
        })
        snackbar.open({
          message: 'KPI editado com sucesso.',
          variant: 'success',
        })
        refresh()
      }
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          (isEdit ? 'Erro ao editar KPI.' : 'Erro ao criar KPI.'),
        variant: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleRemoveApiFile = async (attachmentId) => {
    return await service.dponet.monitoringManagementKpi.destroyAttachment({
      monitoringManagementKpiId: monitoringManagementKpi?.id,
      attachmentId: attachmentId,
    })
  }

  useEffect(() => {
    if (!monitoringManagementKpi) return

    formMethods.reset({
      active: monitoringManagementKpi?.active ?? true,
      name: monitoringManagementKpi?.name || '',
      assigneeId: monitoringManagementKpi?.assignee || null,
      description: monitoringManagementKpi?.description || '',
      departmentId: monitoringManagementKpi?.department?.id || '',
      toDo: monitoringManagementKpi?.toDo || '',
      methodology: monitoringManagementKpi?.methodology || '',
      goal: monitoringManagementKpi?.goal || '',
      goalMin: monitoringManagementKpi?.goalMin || '',
      goalMax: monitoringManagementKpi?.goalMax || '',
      goalOperator: monitoringManagementKpi?.goalOperator || '',
      measurementUnit: monitoringManagementKpi?.measurementUnit || '',
      periodicity: monitoringManagementKpi?.periodicity || '',
      priority: monitoringManagementKpi?.priority || '',
      periodicityDay: monitoringManagementKpi?.periodicityDay || '',
      startDate: monitoringManagementKpi?.startDate || '',
      endDate: monitoringManagementKpi?.endDate || '',
      formula: monitoringManagementKpi?.formula || '',
      dataSource: monitoringManagementKpi?.dataSource || '',
      attachments: monitoringManagementKpi?.attachments || [],
    })
  }, [monitoringManagementKpi])

  return (
    <>
      <LoadingFeedback open={isLoading} />

      <FormContext {...formMethods}>
        <form id="kpi-form" onSubmit={handleSubmit(onSubmit)}>
          <Card title="Informações">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SwitchController
                  control={control}
                  name="active"
                  disabled={isShow}
                  option={{
                    left: false,
                    right: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  as={
                    <TextField
                      label="Nome do KPI *"
                      fullWidth
                      variant="outlined"
                      error={!!errors.name}
                      helperText={errors?.name?.message}
                    />
                  }
                  control={control}
                  name="name"
                  disabled={isShow}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <AutocompleteTypeAndSearch
                  textField={{
                    label: 'Selecione o responsável pelo KPI *',
                    variant: 'outlined',
                    error: !!errors?.assigneeId,
                    helperText: errors?.assigneeId?.message,
                  }}
                  requestToLoadOptions={responseUsers}
                  name="assigneeId"
                  disabled={isShow}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  as={
                    <KeyboardDatePicker
                      fullWidth
                      value={monitoringManagementKpi?.startDate}
                      format="DD/MM/yyyy"
                      label="Data de Início *"
                      helperText={errors?.startDate?.message}
                      error={!!errors.startDate}
                      inputVariant="outlined"
                      onChange={(newValue) => ({ value: newValue })}
                      disabled={isShow}
                    />
                  }
                  control={control}
                  defaultValue={null}
                  name="startDate"
                  mode="onChange"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  as={
                    <KeyboardDatePicker
                      fullWidth
                      value={monitoringManagementKpi?.endDate}
                      format="DD/MM/yyyy"
                      label="Data de Conclusão *"
                      helperText={errors?.endDate?.message}
                      error={!!errors.endDate}
                      inputVariant="outlined"
                      onChange={(newValue) => ({ value: newValue })}
                    />
                  }
                  control={control}
                  defaultValue={null}
                  name="endDate"
                  mode="onChange"
                  disabled={isShow}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  as={
                    <Select
                      label="Departamento *"
                      items={departments}
                      isLoading={isDepartmentsLoading}
                      error={!!errors?.departmentId}
                      helperText={errors?.departmentId?.message}
                    />
                  }
                  name="departmentId"
                  control={control}
                  defaultValue={monitoringManagementKpi?.department?.name}
                  disabled={isShow}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  as={
                    <TextField
                      label="O que será feito *"
                      value={monitoringManagementKpi?.toDO}
                      fullWidth
                      variant="outlined"
                      multiline
                      minRows={3}
                      error={!!errors.toDo}
                      helperText={errors?.toDo?.message}
                    />
                  }
                  control={control}
                  name="toDo"
                  defaultValue={monitoringManagementKpi?.toDO}
                  disabled={isShow}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  as={
                    <TextField
                      label="Metodologia *"
                      value={monitoringManagementKpi?.methodology}
                      fullWidth
                      variant="outlined"
                      multiline
                      minRows={3}
                      error={!!errors.methodology}
                      helperText={errors?.methodology?.message}
                    />
                  }
                  control={control}
                  name="methodology"
                  disabled={isShow}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  as={
                    <TextField
                      value={monitoringManagementKpi?.description}
                      label="Descrição *"
                      fullWidth
                      variant="outlined"
                      multiline
                      minRows={3}
                      error={!!errors.description}
                      helperText={errors?.description?.message}
                    />
                  }
                  control={control}
                  name="description"
                  disabled={isShow}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Controller
                  as={
                    <Select
                      label="Operador da Meta *"
                      fieldValue="value"
                      fieldName="name"
                      items={GOAL_OPERATOR_OPTIONS}
                      error={!!errors?.goalOperator}
                      helperText={errors?.goalOperator?.message}
                    />
                  }
                  name="goalOperator"
                  control={control}
                  defaultValue={monitoringManagementKpi?.goalOperator}
                  disabled={isShow || isEdit}
                />
              </Grid>
              {!isInBetween && (
                <Grid item xs={12} md={2}>
                  <Controller
                    as={
                      <TextField
                        value={monitoringManagementKpi?.goal}
                        label="Meta *"
                        fullWidth
                        variant="outlined"
                        error={!!errors.goal}
                        helperText={errors?.goal?.message}
                      />
                    }
                    control={control}
                    name="goal"
                    disabled={isShow || isEdit}
                  />
                </Grid>
              )}
              {isInBetween && (
                <>
                  <Grid item xs={12} md={1}>
                    <Controller
                      as={
                        <TextField
                          value={monitoringManagementKpi?.goalMin}
                          label="Meta Menor *"
                          fullWidth
                          variant="outlined"
                          error={!!errors.goalMin}
                          helperText={errors?.goalMin?.message}
                        />
                      }
                      control={control}
                      name="goalMin"
                      disabled={isShow || isEdit}
                    />
                  </Grid>
                  <Grid item xs={12} md={1}>
                    <Controller
                      as={
                        <TextField
                          value={monitoringManagementKpi?.goalMax}
                          label="Meta Maior *"
                          fullWidth
                          variant="outlined"
                          error={!!errors.goalMax}
                          helperText={errors?.goalMax?.message}
                        />
                      }
                      control={control}
                      name="goalMax"
                      disabled={isShow || isEdit}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12} md={2}>
                <Controller
                  as={
                    <Select
                      label="Unidade de Medida *"
                      fieldValue="value"
                      fieldName="name"
                      items={MEASUREMENT_UNIT_OPTIONS}
                      error={!!errors?.measurementUnit}
                      helperText={errors?.measurementUnit?.message}
                    />
                  }
                  name="measurementUnit"
                  control={control}
                  defaultValue={monitoringManagementKpi?.measurementUnit}
                  disabled={isShow}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Controller
                  as={
                    <Select
                      label="Periodicidade *"
                      fieldValue="value"
                      fieldName="name"
                      items={PERIODICITY_OPTIONS}
                      error={!!errors?.periodicity}
                      helperText={errors?.periodicity?.message}
                    />
                  }
                  name="periodicity"
                  control={control}
                  defaultValue={monitoringManagementKpi?.periodicity}
                  disabled={isShow || isEdit}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Controller
                  as={
                    <KeyboardDatePicker
                      fullWidth
                      value={monitoringManagementKpi?.periodicityDay}
                      format="DD/MM/yyyy"
                      label="Dia de Periodicidade *"
                      helperText={errors?.periodicityDay?.message}
                      error={!!errors.periodicityDay}
                      inputVariant="outlined"
                      onChange={(newValue) => ({ value: newValue })}
                    />
                  }
                  control={control}
                  defaultValue={null}
                  name="periodicityDay"
                  mode="onChange"
                  disabled={isShow || isEdit}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Controller
                  as={
                    <Select
                      label="Prioridade *"
                      fieldValue="value"
                      fieldName="name"
                      items={PRIORITY_OPTIONS}
                      error={!!errors?.priority}
                      helperText={errors?.priority?.message}
                    />
                  }
                  name="priority"
                  control={control}
                  defaultValue={monitoringManagementKpi?.priority}
                  disabled={isShow}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  as={
                    <TextField
                      label="Fórmula de Cálculo"
                      value={monitoringManagementKpi?.formula}
                      fullWidth
                      variant="outlined"
                      multiline
                      minRows={3}
                      error={!!errors.formula}
                      helperText={errors?.formula?.message}
                      disabled={isShow}
                    />
                  }
                  control={control}
                  name="formula"
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  as={
                    <TextField
                      label="Fonte de Dados"
                      value={monitoringManagementKpi?.dataSource}
                      fullWidth
                      variant="outlined"
                      multiline
                      minRows={3}
                      error={!!errors.dataSource}
                      helperText={errors?.dataSource?.message}
                      disabled={isShow}
                    />
                  }
                  control={control}
                  name="dataSource"
                />
              </Grid>

              <Label
                title="Anexe os documentos relacionados ao KPI"
                xs={12}
                item
              >
                <FileUploadInput
                  controlName="attachments"
                  multiple
                  controllerBar
                  hasDownloadFile
                  onRemoveApiFile={handleRemoveApiFile}
                  disabled={isShow}
                />
              </Label>
            </Grid>
          </Card>
          {!isShow && (
            <Box ml={2} mt={2} display="flex" justifyContent="flex-end">
              {!isEdit && (
                <Box pr={1}>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => history.push(routes.kpi.all)}
                  >
                    Cancelar
                  </Button>
                </Box>
              )}
              <Box>
                <Button
                  form="kpi-form"
                  type="submit"
                  color="primary"
                  variant="contained"
                  disable={isLoading}
                >
                  {isEdit ? 'Salvar' : 'Criar'}
                </Button>
              </Box>
            </Box>
          )}
        </form>
      </FormContext>
    </>
  )
}

export default KpiForm
